import React, { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { api, resolveImage } from "../../lib/api";

const ImageUploader = ({ value, onChange, label = "Image", aspect = "aspect-[4/3]" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await api.upload(file);
      onChange(url);
    } catch (e) {
      alert(e.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      {label && (
        <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">
          {label}
        </div>
      )}
      <div className={`relative ${aspect} rounded-[16px] border border-dashed border-black/25 overflow-hidden bg-[color:var(--bg-2)]`}>
        {value ? (
          <>
            <img src={resolveImage(value)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white grid place-items-center border border-black/10"
              aria-label="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center text-[color:var(--muted)]">
            <ImageIcon className="w-8 h-8" />
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 grid place-items-center bg-black/40 text-white">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/20 text-sm hover:border-black/50 disabled:opacity-60"
        >
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : value ? "Replace" : "Upload from device"}
        </button>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="… or paste an image URL"
          className="flex-1 min-w-[200px] bg-transparent border-b border-black/20 focus:border-[color:var(--coral)] outline-none px-1 py-1 text-sm"
        />
      </div>
    </div>
  );
};

export default ImageUploader;
